
import * as React from 'react';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import VisitorsRegistrationFileEntity from 'Models/Security/Acl/VisitorsRegistrationFileEntity';
import UserRegistrationFileEntity from 'Models/Security/Acl/UserRegistrationFileEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { FileListPreview } from 'Views/Components/CRUD/Attributes/AttributeFile';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IRegistrationFileEntityAttributes extends IModelAttributes {
	fileId: string;
	file: Blob;

	registrationId?: string;
	registration?: Models.RegistrationEntity | Models.IRegistrationEntityAttributes;

}

@entity('RegistrationFileEntity', 'Registration File')
export default class RegistrationFileEntity extends Model
	implements IRegistrationFileEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsRegistrationFileEntity(),
		new UserRegistrationFileEntity(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	
	/**
	 * File
	 */
	@observable
	@attribute<RegistrationFileEntity, string>({ file: 'file' })
	@CRUD({
		name: 'File',
		displayType: 'file',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseUuid,
		fileAttribute: 'file',
		displayFunction: attr => attr
			? <FileListPreview url={attr} />
			: 'No File Attached',
	})
	public fileId: string;

	@observable
	public file: Blob;

	@observable
	@attribute()
	@CRUD({
		
		name: 'Registration',
		displayType: 'reference-combobox',
		order: 20,
		referenceTypeFunc: () => Models.RegistrationEntity,
		
	})
	public registrationId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public registration: Models.RegistrationEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IRegistrationFileEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IRegistrationFileEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.file !== undefined) {
				this.file = attributes.file;
			}
			if (attributes.fileId !== undefined) {
				this.fileId = attributes.fileId;
			}
			if (attributes.registrationId !== undefined) {
				this.registrationId = attributes.registrationId;
			}
			if (attributes.registration !== undefined) {
				if (attributes.registration === null) {
					this.registration = attributes.registration;
				} else if (attributes.registration instanceof Models.RegistrationEntity) {
					this.registration = attributes.registration;
					this.registrationId = attributes.registration.id;
				} else {
					this.registration = new Models.RegistrationEntity(attributes.registration);
					this.registrationId = this.registration.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		registration {
			${Models.RegistrationEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
				contentType: 'multipart/form-data',
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(RegistrationFileEntity.prototype, 'created');
CRUD(modifiedAttr)(RegistrationFileEntity.prototype, 'modified');
